package com.bulut.mecitogullari.ui.screens.reports

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.bulut.mecitogullari.core.i18n.LocalStrings
import com.bulut.mecitogullari.core.i18n.localizedCategory
import com.bulut.mecitogullari.core.util.formatAmount
import com.bulut.mecitogullari.data.model.MonthSummary
import com.bulut.mecitogullari.ui.components.EmptyState
import com.bulut.mecitogullari.ui.components.MonthSelector
import com.bulut.mecitogullari.ui.components.MoneyText
import com.bulut.mecitogullari.ui.components.SectionTitle
import com.bulut.mecitogullari.ui.theme.AppColors
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun ReportsScreen(
    viewModel: ReportsViewModel = koinViewModel(),
) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
    val strings = LocalStrings.current

    var noteDraft by remember { mutableStateOf(uiState.savedNote) }
    var noteJustSaved by remember { mutableStateOf(false) }
    LaunchedEffect(uiState.month, uiState.savedNote) {
        noteDraft = uiState.savedNote
        noteJustSaved = false
    }

    LazyColumn(
        modifier = Modifier.fillMaxSize().statusBarsPadding(),
        contentPadding = PaddingValues(start = 16.dp, end = 16.dp, top = 12.dp, bottom = 32.dp),
        verticalArrangement = Arrangement.spacedBy(14.dp),
    ) {
        item {
            Text(strings.reportsTitle, style = MaterialTheme.typography.titleLarge)
        }

        item {
            MonthSelector(
                month = uiState.month,
                onPrevious = viewModel::onPreviousMonth,
                onNext = viewModel::onNextMonth,
            )
        }

        item { ClosingBalanceCard(uiState.summary) }

        item { SectionTitle(strings.expenseBreakdown) }
        item {
            BreakdownCard(
                entries = uiState.expenseBreakdown,
                barColor = AppColors.Expense,
                emptyMessage = strings.noExpenseThisMonth,
            )
        }

        item { SectionTitle(strings.incomeBreakdown) }
        item {
            BreakdownCard(
                entries = uiState.incomeBreakdown,
                barColor = AppColors.Income,
                emptyMessage = strings.noIncomeThisMonth,
            )
        }

        item { SectionTitle(strings.cashNoteSection) }
        item {
            Card(
                shape = RoundedCornerShape(16.dp),
                colors = CardDefaults.cardColors(containerColor = Color.White),
                elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
            ) {
                Column(modifier = Modifier.padding(14.dp)) {
                    OutlinedTextField(
                        value = noteDraft,
                        onValueChange = {
                            noteDraft = it
                            noteJustSaved = false
                        },
                        placeholder = { Text(strings.cashNotePlaceholder) },
                        minLines = 3,
                        shape = RoundedCornerShape(12.dp),
                        modifier = Modifier.fillMaxWidth(),
                    )
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(top = 10.dp),
                        horizontalArrangement = Arrangement.SpaceBetween,
                    ) {
                        Text(
                            text = if (noteJustSaved) strings.savedCheck else "",
                            style = MaterialTheme.typography.bodySmall,
                            color = AppColors.Income,
                            modifier = Modifier.padding(top = 12.dp),
                        )
                        Button(
                            onClick = {
                                viewModel.saveNote(noteDraft)
                                noteJustSaved = true
                            },
                            shape = RoundedCornerShape(10.dp),
                            colors = ButtonDefaults.buttonColors(containerColor = AppColors.Navy),
                        ) {
                            Text(strings.saveNote)
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ClosingBalanceCard(summary: MonthSummary) {
    val strings = LocalStrings.current
    Card(
        shape = RoundedCornerShape(20.dp),
        colors = CardDefaults.cardColors(containerColor = AppColors.NavyDeep),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
        modifier = Modifier.fillMaxWidth(),
    ) {
        Column(modifier = Modifier.padding(20.dp)) {
            Text(
                text = strings.carryToNextMonth,
                style = MaterialTheme.typography.labelLarge,
                color = Color.White.copy(alpha = 0.8f),
            )
            MoneyText(
                amount = summary.closingBalance,
                color = if (summary.closingBalance >= 0) Color(0xFF4ADE80) else Color(0xFFF87171),
                large = true,
                modifier = Modifier.padding(top = 4.dp),
            )
            HorizontalDivider(
                modifier = Modifier.padding(vertical = 12.dp),
                color = Color.White.copy(alpha = 0.15f),
            )
            SummaryLine(strings.openingBalanceLabel, summary.openingBalance)
            SummaryLine(strings.income, summary.income, Color(0xFF4ADE80))
            SummaryLine(strings.expense, -summary.expense, Color(0xFFF87171))
            SummaryLine(strings.netProfit, summary.netProfit)
        }
    }
}

@Composable
private fun SummaryLine(label: String, amount: Double, valueColor: Color = Color.White) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(vertical = 3.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Text(
            text = label,
            style = MaterialTheme.typography.bodyMedium,
            color = Color.White.copy(alpha = 0.8f),
        )
        Text(
            text = formatAmount(amount),
            style = MaterialTheme.typography.bodyMedium,
            color = valueColor,
        )
    }
}

/** Simple horizontal bar chart: one row per category with share of total. */
@Composable
private fun BreakdownCard(
    entries: List<Pair<String, Double>>,
    barColor: Color,
    emptyMessage: String,
) {
    Card(
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
        modifier = Modifier.fillMaxWidth(),
    ) {
        if (entries.isEmpty()) {
            EmptyState(emptyMessage)
        } else {
            val total = entries.sumOf { it.second }.takeIf { it > 0 } ?: 1.0
            Column(
                modifier = Modifier.padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp),
            ) {
                entries.forEach { (category, amount) ->
                    val fraction = (amount / total).toFloat()
                    Column {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                        ) {
                            Text(
                                text = localizedCategory(category),
                                style = MaterialTheme.typography.bodyMedium,
                            )
                            Text(
                                text = "${formatAmount(amount)}  •  %${(fraction * 100).toInt()}",
                                style = MaterialTheme.typography.bodySmall,
                                color = AppColors.TextSecondary,
                            )
                        }
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(top = 6.dp)
                                .height(8.dp)
                                .background(AppColors.Outline, RoundedCornerShape(4.dp)),
                        ) {
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth(fraction.coerceIn(0.02f, 1f))
                                    .height(8.dp)
                                    .background(barColor, RoundedCornerShape(4.dp)),
                            )
                        }
                    }
                }
            }
        }
    }
}
